import {
    ICorporationResponse,
    IDeskActivityOverviewStats,
} from '@kcftech/prometheus-client';
import { useQuery } from '@tanstack/react-query';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { sdClient } from 'contexts/SDKClientContext';
import { useDebounce } from 'custom_hooks/useDebounce';
import { DashboardHeader } from 'pages/Dashboard/components/DashboardHeader';
import { EngagementMetrics } from 'pages/Dashboard/components/filter_button/EngagementMetrics';
import { FaultTypesFilterButton } from 'pages/Dashboard/components/filter_button/FaultTypesFilterButton';
import { LocationFilterButton } from 'pages/Dashboard/components/filter_button/LocationFilterButton';
import { SeverityFilterButton } from 'pages/Dashboard/components/filter_button/SeverityFilterButton';
import { StatusFilterButton } from 'pages/Dashboard/components/filter_button/StatusFilterButton';
import { CardHeader } from 'pages/Dashboard/components/styled';
import { ActivityTypes } from 'pages/Dashboard/DeskActivityOverviewDashboard/Charts/ActivityTypes';
import { AssetFaultsBySeverity } from 'pages/Dashboard/DeskActivityOverviewDashboard/Charts/AssetFaultsBySeverity';
import { FaultTypesByCount } from 'pages/Dashboard/DeskActivityOverviewDashboard/Charts/FaultTypesByCount';
import { OpenClosedNewTickets } from 'pages/Dashboard/DeskActivityOverviewDashboard/Charts/OpenClosedNewTickets';
import {
    Card,
    CardContent,
    CardTitle,
    Page,
} from 'pages/Dashboard/DeskActivityOverviewDashboard/components/styled';
import {
    DashboardTimeRangePreset,
    presetToDateRange,
    TimeRangeFilterButton,
} from 'pages/Dashboard/DeskActivityOverviewDashboard/components/TimeRangeFilterButton';
import { useDashboardFiltersStore } from 'pages/Dashboard/DeskActivityOverviewDashboard/store/useDashboardFiltersStore';
import { memo, useCallback } from 'react';

type DashboardCardProps = {
    data: IDeskActivityOverviewStats | undefined;
};

const FaultTypesByCountCard = memo(function FaultTypesByCountCard({
    data,
}: DashboardCardProps) {
    return (
        <Card className="fault-types">
            <CardHeader title="Fault Types" description="Number of each fault type" />
            <CardContent $contentPositioning="stretch">
                <SpinnerArea isLoading={!data?.FaultTypes} noContainer>
                    {!!data?.FaultTypes && <FaultTypesByCount data={data.FaultTypes} />}
                </SpinnerArea>
            </CardContent>
        </Card>
    );
});

const AssetFaultsBySeverityCard = memo(function AssetFaultsBySeverityCard({
    data,
}: DashboardCardProps) {
    return (
        <Card className="asset-faults">
            <CardHeader
                title="Asset Faults by Asset Type"
                description={
                    <>Faults per Asset Type, broken down by the fault&apos;s Severity</>
                }
            />
            <CardContent $contentPositioning="stretch">
                <SpinnerArea isLoading={!data?.AssetFaultsBySeverity} noContainer>
                    {!!data?.AssetFaultsBySeverity && (
                        <AssetFaultsBySeverity
                            assetData={data.AssetFaultsBySeverity}
                            overallSeverity={data.OverallSeverityDistribution}
                        />
                    )}
                </SpinnerArea>
            </CardContent>
        </Card>
    );
});

const EngagementMetricsCard = memo(function EngagementMetricsCard({
    data,
}: DashboardCardProps) {
    return (
        <Card className="engagement">
            <CardTitle>Engagement</CardTitle>
            <EngagementMetrics data={data?.Engagement} />
        </Card>
    );
});

const OpenVsClosedTicketsCard = memo(function OpenVsClosedTicketsCard({
    data,
}: DashboardCardProps) {
    return (
        <Card className="open-vs-closed">
            <CardHeader
                title="Open vs Closed Tickets"
                description="Number of tickets created "
            />
            <CardContent $contentPositioning="stretch">
                <SpinnerArea isLoading={!data?.TicketStatusByLocation} noContainer>
                    {!!data?.TicketStatusByLocation && (
                        <OpenClosedNewTickets data={data.TicketStatusByLocation} />
                    )}
                </SpinnerArea>
            </CardContent>
        </Card>
    );
});

const ActivityTypesCard = memo(function ActivityTypesCard({ data }: DashboardCardProps) {
    return (
        <Card className="activity-types">
            <CardHeader
                title="Activity Types"
                description={
                    <>
                        <span>
                            Identifies when in the lifecycle of a fault the action is
                            being taken.
                        </span>
                        <ul>
                            <li>
                                <strong>Corrective</strong> action implies that one or
                                more components needed to be changed due to irreversible
                                damage (i.e. Bearing replacement)
                            </li>
                            <li>
                                <strong>Proactive</strong> action imples that the health
                                condition was corrected at a reversible state preventing
                                future collateral damage (i.e. Misalignment)
                            </li>
                            <li>
                                <strong>Predictive</strong> action implies that process or
                                system parameters were adjusted that may have eventually
                                produced a negative effect on the equipment (i.e.
                                Resonance)
                            </li>
                        </ul>
                    </>
                }
            />
            <CardContent $contentPositioning="stretch">
                <SpinnerArea isLoading={!data?.ActivityTypes} noContainer>
                    {!!data?.ActivityTypes && <ActivityTypes data={data.ActivityTypes} />}
                </SpinnerArea>
            </CardContent>
        </Card>
    );
});

export type DeskActivityOverviewDashboardProps = {
    corporation: ICorporationResponse;
};

export function DeskActivityOverviewDashboard({
    corporation,
}: DeskActivityOverviewDashboardProps) {
    const {
        startDateTime,
        endDateTime,
        locationIds,
        severities,
        statuses,
        faultTypeIds,
        timeRangePreset,
        setStartDateTime,
        setEndDateTime,
        setLocationIds,
        setSeverities,
        setStatuses,
        setFaultTypeIds,
        setTimeRangePreset,
    } = useDashboardFiltersStore();

    // Debounce the start date to avoid spamming the API while sliding
    const debouncedStartDateTime = useDebounce(startDateTime, 300);

    const handleTimeRangePresetChange = useCallback(
        (preset: DashboardTimeRangePreset) => {
            setTimeRangePreset(preset);

            const range = presetToDateRange(preset);
            setStartDateTime(range.startDateTime);
            setEndDateTime(range.endDateTime);
        },
        [setTimeRangePreset, setStartDateTime, setEndDateTime],
    );

    const { data, refetch: _refetch } = useQuery({
        queryKey: [
            'desk-activity-overview',
            corporation.Id,
            debouncedStartDateTime,
            endDateTime,
            locationIds,
            severities,
            statuses,
            faultTypeIds,
        ],
        queryFn: () =>
            sdClient.getDeskActivityOverviewForCorporation({
                corporationId: corporation.Id,
                startDateTime: debouncedStartDateTime,
                endDateTime: endDateTime ?? new Date(),
                locationIds,
                severities,
                statuses,
                faultTypeIds,
            }),
        placeholderData: (prev) => prev,
    });

    // used to correctly provide `availableFaultTypes`
    const { data: unfilteredData, refetch: _refetchUnfilteredData } = useQuery({
        queryKey: [
            'desk-activity-overview',
            corporation.Id,
            debouncedStartDateTime,
            endDateTime,
            locationIds,
        ],
        queryFn: () =>
            sdClient.getDeskActivityOverviewForCorporation({
                corporationId: corporation.Id,
                startDateTime: debouncedStartDateTime,
                endDateTime: endDateTime ?? new Date(),
                locationIds: locationIds,
            }),
        placeholderData: (prev) => prev,
    });

    return (
        <Page>
            <DashboardHeader
                corporation={corporation}
                currentDashboard="desk-activity-overview"
            >
                <SeverityFilterButton
                    severities={severities}
                    onChangeSeverities={setSeverities}
                />
                <StatusFilterButton statuses={statuses} onChangeStatuses={setStatuses} />
                <FaultTypesFilterButton
                    faultTypeIds={faultTypeIds}
                    onChangeFaultTypeIds={setFaultTypeIds}
                    availableFaultTypes={unfilteredData?.FaultTypes ?? []}
                />
                <LocationFilterButton
                    corporationId={corporation.Id}
                    locationIds={locationIds}
                    onChangeLocationIds={setLocationIds}
                />
                <TimeRangeFilterButton
                    selectedPreset={timeRangePreset}
                    onChangePreset={handleTimeRangePresetChange}
                />
            </DashboardHeader>

            <FaultTypesByCountCard data={data} />
            <AssetFaultsBySeverityCard data={data} />
            <EngagementMetricsCard data={data} />
            <OpenVsClosedTicketsCard data={data} />
            <ActivityTypesCard data={data} />
        </Page>
    );
}
